import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createSimulator } from "@/lib/api/simulators";
import { getErrorMessage } from "@/lib/api/client";
import { slugify } from "@/lib/utils";

const schema = z.object({
  name: z.string().min(1, "Le nom est requis").max(255),
  slug: z
    .string()
    .min(1, "Le slug est requis")
    .max(255)
    .regex(/^[a-z0-9_]+$/, "Le slug ne peut contenir que des lettres minuscules, chiffres et underscores"),
  description: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface CreateSimulatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSimulatorDialog({
  open,
  onOpenChange,
}: CreateSimulatorDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      slug: "",
      description: "",
    },
  });

  const name = watch("name");

  const mutation = useMutation({
    mutationFn: createSimulator,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["simulators"] });
      toast.success("Simulateur créé", {
        description: `Le simulateur "${data.name}" a été créé avec succès`,
      });
      reset();
      onOpenChange(false);
      navigate({ to: '/admin/simulators/$id', params: { id: String(data.id) } });
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  const onSubmit = (data: FormData) => {
    mutation.mutate(data);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setValue("name", newName);
    setValue("slug", slugify(newName));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveau simulateur</DialogTitle>
          <DialogDescription>
            Créez un nouveau simulateur de pricing
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nom</Label>
            <Input
              id="name"
              placeholder="Mon simulateur"
              {...register("name")}
              onChange={handleNameChange}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              placeholder="mon-simulateur"
              {...register("slug")}
            />
            {errors.slug && (
              <p className="text-sm text-destructive">{errors.slug.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Identifiant unique utilisé dans les URLs
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optionnel)</Label>
            <Textarea
              id="description"
              placeholder="Description du simulateur..."
              {...register("description")}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Création..." : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

